import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link as RouterLink } from "react-router-dom";
import {
  Box, Heading, Flex, Button, SimpleGrid, Spinner, Tag, Badge, HStack, Text,
  Input, Select, useToast, useDisclosure, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalBody, ModalFooter, ModalCloseButton, FormControl, FormLabel,
  Textarea, Avatar, AvatarGroup, Progress, IconButton, Tabs, TabList, TabPanels,
  Tab, TabPanel, Stat, StatLabel, StatNumber, Divider, Menu, MenuButton, MenuList,
  MenuItem, Editable, EditableInput, EditablePreview,
} from "@chakra-ui/react";
import { ArrowBackIcon, DeleteIcon, AddIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { api } from "../../lib/api";
import { useShellHeader } from "../../Components/AppShell";
import KanbanBoard from "../Tasks/KanbanBoard";
import TaskDetailDrawer from "../Tasks/TaskDetailDrawer";
import TaskCreateModalV2 from "../Tasks/TaskCreateModalV2";
import DepartmentCards from "./DepartmentCards";

const STATUSES = ["planning", "active", "on_hold", "completed", "archived"];
const STATUS_COLORS = { planning: "gray", active: "blue", on_hold: "orange", completed: "green", archived: "purple" };

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allUsers, setAllUsers] = useState([]);
  const memberModal = useDisclosure();
  const createTask = useDisclosure();
  const taskDrawer = useDisclosure();
  const [openTaskId, setOpenTaskId] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [pRes, tRes, uRes] = await Promise.all([
        api(`/projects/${id}`),
        api(`/tasks?projectId=${id}`),
        api("/admin/users").catch(() => ({ users: [] })),
      ]);
      setProject(pRes.project);
      setTasks(tRes.tasks || []);
      setAllUsers(uRes.users || []);
    } catch (e) {
      toast({ title: e.message, status: "error" });
      if (e.status === 403 || e.status === 404) navigate("/projects");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  async function loadUsers() {
    try {
      const r = await api("/admin/users").catch(() => ({ users: [] }));
      setAllUsers(r.users || []);
    } catch (e) {}
  }

  async function patchProject(body) {
    try {
      const r = await api(`/projects/${id}`, { method: "PATCH", body: JSON.stringify(body) });
      setProject((prev) => ({ ...prev, ...r.project }));
      toast({ title: "Updated", status: "success", duration: 1200 });
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  async function addMember(userId) {
    try {
      await api(`/projects/${id}/members`, { method: "POST", body: JSON.stringify({ userId }) });
      toast({ title: "Member added", status: "success" });
      load();
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  async function removeMember(userId) {
    if (!window.confirm("Remove this member?")) return;
    try {
      await api(`/projects/${id}/members/${userId}`, { method: "DELETE" });
      toast({ title: "Removed", status: "success" });
      load();
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  async function deleteProject() {
    if (!window.confirm("Delete this project? Tasks remain but become unlinked.")) return;
    try {
      await api(`/projects/${id}`, { method: "DELETE" });
      toast({ title: "Project deleted", status: "success" });
      navigate("/projects");
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  useShellHeader(
    project ? { title: project.name, breadcrumb: `Projects / ${project.key}` } : { title: "Loading..." },
    [project?.name, project?.key]
  );

  if (loading || !project) return <Spinner />;

  const { progress = 0, taskCount = 0, doneCount = 0, statusCounts = {} } = project;
  const memberIds = new Set((project.members || []).map((m) => String(m._id)));
  const candidates = allUsers.filter((u) => !memberIds.has(String(u._id)));

  return (
    <Box>
      <Flex bg="white" p={6} rounded="md" shadow="sm" mb={6} align="start" borderTop="4px solid" borderColor={project.color}>
        <Box flex="1">
          <Editable defaultValue={project.name} onSubmit={(v) => v !== project.name && patchProject({ name: v })}>
            <EditablePreview as={Heading} size="lg" />
            <EditableInput />
          </Editable>
          <HStack mt={2} spacing={2} wrap="wrap">
            <Tag>{project.key}</Tag>
            <Menu>
              <MenuButton as={Button} size="sm" rightIcon={<ChevronDownIcon />} colorScheme={STATUS_COLORS[project.status]}>
                {project.status.replace("_", " ")}
              </MenuButton>
              <MenuList>
                {STATUSES.map((s) => <MenuItem key={s} onClick={() => patchProject({ status: s })}>{s.replace("_", " ")}</MenuItem>)}
              </MenuList>
            </Menu>
            {project.team && <Tag bg={project.team.color || "gray.300"} color="white">{project.team.name}</Tag>}
            {project.department && <Tag bg={project.department.color || "gray.500"} color="white">{project.department.name}</Tag>}
            {project.dueDate && <Tag>Due {new Date(project.dueDate).toLocaleDateString()}</Tag>}
          </HStack>
          <Text mt={3} color="gray.600" fontSize="sm">{project.description || "—"}</Text>
        </Box>
        <Menu>
          <MenuButton as={IconButton} icon={<ChevronDownIcon />} variant="ghost" />
          <MenuList>
            <MenuItem onClick={() => patchProject({ archived: !project.archivedAt })}>
              {project.archivedAt ? "Unarchive" : "Archive"}
            </MenuItem>
            <MenuItem color="red.500" onClick={deleteProject}>Delete project</MenuItem>
          </MenuList>
        </Menu>
      </Flex>

      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
        <Stat bg="white" p={4} rounded="md" shadow="sm"><StatLabel>Progress</StatLabel><StatNumber>{progress}%</StatNumber>
          <Progress value={progress} size="xs" mt={2} colorScheme={progress === 100 ? "green" : "blue"} rounded="full" /></Stat>
        <Stat bg="white" p={4} rounded="md" shadow="sm"><StatLabel>Tasks</StatLabel><StatNumber>{taskCount}</StatNumber></Stat>
        <Stat bg="white" p={4} rounded="md" shadow="sm"><StatLabel>Done</StatLabel><StatNumber color="green.500">{doneCount}</StatNumber></Stat>
        <Stat bg="white" p={4} rounded="md" shadow="sm"><StatLabel>Members</StatLabel><StatNumber>{(project.members || []).length}</StatNumber></Stat>
      </SimpleGrid>

      <Tabs bg="white" rounded="md" shadow="sm">
        <TabList>
          <Tab>Departments</Tab>
          <Tab>All Tasks ({tasks.length})</Tab>
          <Tab>Members ({(project.members || []).length})</Tab>
          <Tab>Overview</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <DepartmentCards
              projectId={id}
              canManage={localStorage.getItem("role") === "SUPER_ADMIN" || String(project.owner?._id || project.owner) === String(localStorage.getItem("userID"))}
            />
          </TabPanel>
          <TabPanel>
            <HStack justify="space-between" mb={4}>
              <Heading size="sm">{tasks.length} tasks across all departments</Heading>
              <Button leftIcon={<AddIcon />} size="sm" colorScheme="blue" onClick={createTask.onOpen}>Add Task</Button>
            </HStack>
            {tasks.length === 0 ? (
              <Text color="gray.500" textAlign="center" py={6}>No tasks yet.</Text>
            ) : (
              <KanbanBoard
                tasks={tasks}
                onChange={setTasks}
                onTaskClick={(t) => { setOpenTaskId(t._id); taskDrawer.onOpen(); }}
              />
            )}
          </TabPanel>

          <TabPanel>
            <HStack justify="space-between" mb={4}>
              <Heading size="sm">Members</Heading>
              <Button leftIcon={<AddIcon />} size="sm" onClick={() => { loadUsers(); memberModal.onOpen(); }}>Add Member</Button>
            </HStack>
            {(project.members || []).map((m) => {
              const isOwner = String(project.owner._id || project.owner) === String(m._id);
              return (
                <HStack key={m._id} p={3} borderBottom="1px solid" borderColor="gray.100" justify="space-between">
                  <HStack>
                    <Avatar size="sm" name={m.username} />
                    <Box>
                      <Text fontWeight="600">{m.username} {isOwner && <Badge ml={2} colorScheme="purple">Owner</Badge>}</Text>
                      <Text fontSize="xs" color="gray.500">{m.designation || m.email}</Text>
                    </Box>
                  </HStack>
                  {!isOwner && <IconButton size="sm" colorScheme="red" variant="ghost" icon={<DeleteIcon />} aria-label="remove" onClick={() => removeMember(m._id)} />}
                </HStack>
              );
            })}
          </TabPanel>

          <TabPanel>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box>
                <Heading size="xs" color="gray.600" textTransform="uppercase" mb={2}>Owner</Heading>
                <HStack><Avatar size="sm" name={project.owner?.username} /><Text>{project.owner?.username}</Text></HStack>
              </Box>
              <Box>
                <Heading size="xs" color="gray.600" textTransform="uppercase" mb={2}>Status breakdown</Heading>
                <HStack spacing={3} wrap="wrap">
                  {Object.entries(statusCounts || {}).map(([k, v]) => <Tag key={k}>{k}: {v}</Tag>)}
                  {Object.keys(statusCounts || {}).length === 0 && <Text fontSize="sm" color="gray.500">No tasks yet</Text>}
                </HStack>
              </Box>
              <Box>
                <Heading size="xs" color="gray.600" textTransform="uppercase" mb={2}>Start date</Heading>
                <Text fontSize="sm">{project.startDate ? new Date(project.startDate).toLocaleDateString() : "—"}</Text>
              </Box>
              <Box>
                <Heading size="xs" color="gray.600" textTransform="uppercase" mb={2}>Due date</Heading>
                <Text fontSize="sm">{project.dueDate ? new Date(project.dueDate).toLocaleDateString() : "—"}</Text>
              </Box>
            </SimpleGrid>
          </TabPanel>
        </TabPanels>
      </Tabs>

      <Modal isOpen={memberModal.isOpen} onClose={memberModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add member</ModalHeader><ModalCloseButton />
          <ModalBody>
            {candidates.length === 0 ? (
              <Text color="gray.500">No more users to add.</Text>
            ) : candidates.map((u) => (
              <HStack key={u._id} p={2} _hover={{ bg: "gray.50" }} rounded="md" justify="space-between">
                <HStack>
                  <Avatar size="sm" name={u.username} />
                  <Box>
                    <Text fontWeight="600">{u.username}</Text>
                    <Text fontSize="xs" color="gray.500">{u.email}</Text>
                  </Box>
                </HStack>
                <Button size="sm" onClick={() => { addMember(u._id); memberModal.onClose(); }}>Add</Button>
              </HStack>
            ))}
          </ModalBody>
          <ModalFooter><Button variant="ghost" onClick={memberModal.onClose}>Close</Button></ModalFooter>
        </ModalContent>
      </Modal>

      <TaskCreateModalV2
        isOpen={createTask.isOpen}
        onClose={createTask.onClose}
        projects={[project]}
        users={allUsers}
        defaultProjectId={project._id}
        onCreated={(t) => setTasks((prev) => [t, ...prev])}
      />

      <TaskDetailDrawer
        isOpen={taskDrawer.isOpen}
        onClose={() => { taskDrawer.onClose(); setOpenTaskId(null); load(); }}
        taskId={openTaskId}
        allUsers={allUsers}
        onChanged={(updated) => {
          if (updated == null) {
            setTasks((prev) => prev.filter((t) => t._id !== openTaskId));
          } else {
            setTasks((prev) => prev.map((t) => (t._id === updated._id ? updated : t)));
          }
        }}
      />
    </Box>
  );
}
